import { formatDistanceToNow } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import type { NotificationSummary } from "@/types/notifications";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const formatType = (value: string) =>
  value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

const getDateFnsLocale = (language?: string) => {
  const normalized = language?.toLowerCase() ?? "es";

  if (normalized.startsWith("pt")) {
    return ptBR;
  }

  if (normalized.startsWith("en")) {
    return enUS;
  }

  return es;
};

const translate = (
  t: TranslateFn,
  key: string,
  defaultValue: string,
  options: Record<string, unknown> = {}
) => t(key, { ...options, defaultValue });

const extractRegexMatch = (pattern: RegExp, value: string) =>
  pattern.exec(value)?.[1]?.trim() ?? "";

const getProjectName = (notification: NotificationSummary) => {
  if (typeof notification.data?.project_name === "string") {
    return notification.data.project_name;
  }

  return extractRegexMatch(/^Project Created:\s*(.+)$/i, notification.title);
};

const getMemberName = (notification: NotificationSummary) => {
  if (typeof notification.data?.member_name === "string") {
    return notification.data.member_name;
  }

  return extractRegexMatch(
    /^(.+?) has been added to the project\.?$/i,
    notification.message
  );
};

export const formatNotificationTimeAgo = (
  createdAt: string | null | undefined,
  language?: string
) => {
  if (!createdAt) {
    return "";
  }

  return formatDistanceToNow(new Date(createdAt), {
    addSuffix: true,
    locale: getDateFnsLocale(language),
  });
};

export const getNotificationTypeLabel = (
  notificationType: string,
  t: TranslateFn
) =>
  translate(
    t,
    `pages:notifications.types.${notificationType}`,
    formatType(notificationType)
  );

export const getLocalizedNotificationContent = (
  notification: NotificationSummary,
  t: TranslateFn
) => {
  if (notification.notification_type === "project_created") {
    const projectName = getProjectName(notification);

    if (projectName) {
      return {
        title: translate(
          t,
          "pages:notifications.content.project_created.title",
          `Project Created: ${projectName}`,
          { name: projectName }
        ),
        message: translate(
          t,
          "pages:notifications.content.project_created.message",
          `Your project "${projectName}" has been created successfully.`,
          { name: projectName }
        ),
      };
    }
  }

  if (
    notification.notification_type === "activity_update" &&
    notification.data?.activity_type === "member_added"
  ) {
    const memberName = getMemberName(notification);

    return {
      title: translate(
        t,
        "pages:notifications.content.member_added.title",
        "Member Added"
      ),
      message: memberName
        ? translate(
            t,
            "pages:notifications.content.member_added.message",
            `${memberName} has been added to the project.`,
            { name: memberName }
          )
        : translate(
            t,
            "pages:notifications.content.member_added.message_generic",
            "A member has been added to the project."
          ),
    };
  }

  return {
    title: notification.title,
    message: notification.message,
  };
};
